import type { Locale, StatusCategory, StatusRef, WorkTypeCode } from "../api/types";
import { langFor } from "../lib/thai";
import { pickStatusName, useLocale } from "../lib/locale-context";
import { badgeDescription, badgeFor, type BadgeModifier } from "../workflow/badges";

type IconKind = StatusCategory | BadgeModifier | "live";

/** 10px glyphs (viewBox stays 12x12 so every path's coordinates need no change) — the label and the hidden
 * description carry the meaning, never the colour alone. 2026-09-18 UX review: at 12px these sat noticeably
 * larger/bolder than every other icon at this size elsewhere in the app (e.g. the 14px clock icon on "Log in
 * timesheet", whose own circle has similar proportions but far more surrounding padding). */
function BadgeIcon({ kind }: { kind: IconKind }) {
  const common = { width: 10, height: 10, viewBox: "0 0 12 12", "aria-hidden": true, focusable: false } as const;
  switch (kind) {
    case "live":
      return (
        <svg {...common}>
          <circle cx="6" cy="6" r="4.25" fill="none" stroke="currentColor" strokeWidth="1.2" />
          <circle cx="6" cy="6" r="2.4" fill="currentColor" />
        </svg>
      );
    case "todo":
      return (
        <svg {...common}>
          <circle cx="6" cy="6" r="4.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      );
    case "in_progress":
      return (
        <svg {...common}>
          <circle cx="6" cy="6" r="4.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M6 1.75a4.25 4.25 0 0 1 0 8.5z" fill="currentColor" />
        </svg>
      );
    case "done":
    case "success":
      return (
        <svg {...common}>
          <circle cx="6" cy="6" r="5" fill="currentColor" />
          <path d="M3.6 6.1l1.6 1.6 3.2-3.3" fill="none" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "negative":
      return (
        <svg {...common}>
          <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      );
    case "alert":
      return (
        <svg {...common}>
          <path d="M6 1.2l5 9.3H1z" fill="currentColor" />
          <path d="M6 4.6v2.6M6 8.6v.1" stroke="#fff" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
      );
    case "hold":
      return (
        <svg {...common}>
          <rect x="1.5" y="1.5" width="9" height="9" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
          <path d="M4.8 4v4M7.2 4v4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
      );
    case "waiting":
      return (
        <svg {...common}>
          <path d="M3 1.5h6M3 10.5h6M3.5 1.5c0 2.5 5 2.5 5 4.5s-5 2-5 4.5M8.5 1.5c0 2.5-5 2.5-5 4.5s5 2 5 4.5" fill="none" stroke="currentColor" strokeWidth="1.1" />
        </svg>
      );
  }
}

interface StatusBadgeProps {
  status: StatusRef;
  /** The item's work type: the same status name can carry a different modifier per type. */
  type?: WorkTypeCode;
  /** Alias of `type` (the name stream W2's kit badge used). */
  workType?: WorkTypeCode;
  /** Overrides the signed-in user's language. */
  locale?: Locale;
}

/** Status badge: icon + name (English for every user, see `statusLocale()`) + category and modifier in words (UX-01 4.10). */
export function StatusBadge({ status, type, workType, locale: localeOverride }: StatusBadgeProps) {
  const contextLocale = useLocale();
  const locale = localeOverride ?? contextLocale;
  const spec = badgeFor(type ?? workType, status.code);
  const label = pickStatusName(locale, status);
  const classes = ["pill", `cat-${status.category}`, spec.modifier ? `mod-${spec.modifier}` : ""].filter(Boolean).join(" ");
  const description = badgeDescription(status.category, spec);
  return (
    <span className={classes} title={spec.hint ?? `Status category: ${description}`} data-status={status.code}>
      <BadgeIcon kind={spec.live ? "live" : spec.modifier && spec.modifier !== "success" ? spec.modifier : status.category} />
      <span className="vh">Status: </span>
      <span lang={langFor(label)}>{label}</span>
      <span className="vh">, {description}</span>
    </span>
  );
}
